import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Separator } from '@/components/ui/separator';
import { Download, FileText, Calendar, User } from 'lucide-react';
import { toast } from 'sonner';

interface ConsensoData {
  nome: string;
  cognome: string;
  dataNascita: string;
  luogoNascita: string;
  codiceFiscale: string;
  indirizzo: string;
  telefono: string;
  email: string;
  consensoTrattamento: boolean;
  consensoDati: boolean;
  data: string;
}

const ConsensoInformato = () => {
  const [formData, setFormData] = useState<ConsensoData>({
    nome: '',
    cognome: '',
    dataNascita: '',
    luogoNascita: '',
    codiceFiscale: '',
    indirizzo: '',
    telefono: '',
    email: '',
    consensoTrattamento: false,
    consensoDati: false,
    data: new Date().toISOString().split('T')[0]
  });

  const handleInputChange = (field: keyof ConsensoData, value: string | boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const generaPDF = () => {
    // Crea il contenuto HTML per la stampa
    const contenutoHTML = `
      <!DOCTYPE html>
      <html lang="it">
      <head>
        <meta charset="UTF-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <title>Consenso Informato - Fisioterapia</title>
        <style>
          body {
            font-family: Arial, sans-serif;
            line-height: 1.6;
            margin: 20px;
            color: #333;
          }
          .header {
            text-align: center;
            margin-bottom: 30px;
            border-bottom: 2px solid #2563eb;
            padding-bottom: 20px;
          }
          .logo {
            font-size: 24px;
            font-weight: bold;
            color: #2563eb;
            margin-bottom: 10px;
          }
          .subtitle {
            color: #666;
            font-size: 14px;
          }
          .section {
            margin-bottom: 25px;
          }
          .section-title {
            font-size: 18px;
            font-weight: bold;
            color: #2563eb;
            margin-bottom: 15px;
            border-bottom: 1px solid #e5e7eb;
            padding-bottom: 5px;
          }
          .patient-info {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 15px;
            margin-bottom: 20px;
          }
          .field {
            margin-bottom: 10px;
          }
          .field-label {
            font-weight: bold;
            display: inline-block;
            width: 150px;
          }
          .field-value {
            border-bottom: 1px solid #333;
            display: inline-block;
            min-width: 200px;
            padding-bottom: 2px;
          }
          .checkbox-section {
            margin: 20px 0;
            padding: 15px;
            background-color: #f8fafc;
            border-left: 4px solid #2563eb;
          }
          .signature-section {
            margin-top: 40px;
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 50px;
          }
          .signature-box {
            text-align: center;
            border-top: 1px solid #333;
            padding-top: 10px;
            margin-top: 50px;
          }
          .content-text {
            text-align: justify;
            margin-bottom: 15px;
          }
          .important {
            background-color: #fef3c7;
            padding: 15px;
            border-left: 4px solid #f59e0b;
            margin: 20px 0;
          }
          @media print {
            body { margin: 0; }
            .no-print { display: none; }
          }
        </style>
      </head>
      <body>
        <div class="header">
          <div class="logo">CENTRO DI RIABILITAZIONE PAPA</div>
          <div class="subtitle">
            Dott. Peppino S. Papa - Fisioterapista<br>
            Via Nazionale Terme, 191 - 98050 Terme Vigliatore (ME)<br>
            Tel: <a href="tel:+393475391433" className="text-primary hover:underline">+39 347 5391433</a> - Email: <a href="mailto:info@riabilitazionemotoriapapa.it" className="text-primary hover:underline">info@riabilitazionemotoriapapa.it</a><br>
            Iscr. Albo TSRM-PSTRP n. ME 67
          </div>
        </div>

        <h1 style="text-align: center; color: #2563eb; margin-bottom: 30px;">
          CONSENSO INFORMATO PER TRATTAMENTO FISIOTERAPICO
        </h1>

        <div class="section">
          <div class="section-title">DATI DEL PAZIENTE</div>
          <div class="patient-info">
            <div class="field">
              <span class="field-label">Nome:</span>
              <span class="field-value">${formData.nome}</span>
            </div>
            <div class="field">
              <span class="field-label">Cognome:</span>
              <span class="field-value">${formData.cognome}</span>
            </div>
            <div class="field">
              <span class="field-label">Data di nascita:</span>
              <span class="field-value">${formData.dataNascita}</span>
            </div>
            <div class="field">
              <span class="field-label">Luogo di nascita:</span>
              <span class="field-value">${formData.luogoNascita}</span>
            </div>
            <div class="field">
              <span class="field-label">Codice Fiscale:</span>
              <span class="field-value">${formData.codiceFiscale}</span>
            </div>
            <div class="field">
              <span class="field-label">Telefono:</span>
              <span class="field-value">${formData.telefono}</span>
            </div>
          </div>
          <div class="field">
            <span class="field-label">Indirizzo:</span>
            <span class="field-value" style="min-width: 400px;">${formData.indirizzo}</span>
          </div>
          <div class="field">
            <span class="field-label">Email:</span>
            <span class="field-value" style="min-width: 300px;">${formData.email}</span>
          </div>
        </div>

        <div class="section">
          <div class="section-title">INFORMAZIONI SUL TRATTAMENTO FISIOTERAPICO</div>
          <div class="content-text">
            <strong>Natura del trattamento:</strong> Il trattamento fisioterapico consiste nell'applicazione di tecniche manuali, esercizi terapeutici, terapie fisiche e strumentali finalizzate al recupero funzionale, alla riduzione del dolore e al miglioramento della qualità di vita del paziente.
          </div>
          <div class="content-text">
            <strong>Obiettivi del trattamento:</strong> Gli obiettivi specifici verranno definiti in base alla valutazione clinica iniziale e potranno includere: riduzione del dolore, miglioramento della mobilità articolare, rinforzo muscolare, rieducazione posturale, recupero funzionale post-traumatico o post-chirurgico.
          </div>
          <div class="content-text">
            <strong>Modalità di esecuzione:</strong> Il trattamento potrà prevedere terapia manuale, mobilizzazioni articolari, massoterapia, esercizi terapeutici attivi e passivi, terapie fisiche (elettroterapia, ultrasuoni, laser, etc.), rieducazione posturale e funzionale.
          </div>
        </div>

        <div class="section">
          <div class="section-title">BENEFICI ATTESI</div>
          <div class="content-text">
            Il trattamento fisioterapico, se eseguito correttamente e con la collaborazione del paziente, può determinare:
            <ul>
              <li>Riduzione o eliminazione del dolore</li>
              <li>Miglioramento della mobilità articolare e della flessibilità</li>
              <li>Incremento della forza muscolare</li>
              <li>Miglioramento della postura e dell'equilibrio</li>
              <li>Recupero della funzionalità nelle attività quotidiane</li>
              <li>Prevenzione di recidive e complicazioni</li>
            </ul>
          </div>
        </div>

        <div class="section">
          <div class="section-title">RISCHI E POSSIBILI COMPLICAZIONI</div>
          <div class="content-text">
            Come ogni trattamento sanitario, anche la fisioterapia può comportare alcuni rischi, generalmente lievi e transitori:
            <ul>
              <li>Temporaneo aumento del dolore nelle prime sedute</li>
              <li>Affaticamento muscolare</li>
              <li>Lieve gonfiore o arrossamento nella zona trattata</li>
              <li>Raramente: contratture muscolari, vertigini, nausea</li>
              <li>Possibili reazioni cutanee alle terapie fisiche strumentali</li>
            </ul>
          </div>
          <div class="important">
            <strong>IMPORTANTE:</strong> È fondamentale comunicare immediatamente al fisioterapista qualsiasi sintomo anomalo o peggioramento delle condizioni durante o dopo il trattamento.
          </div>
        </div>

        <div class="section">
          <div class="section-title">ALTERNATIVE TERAPEUTICHE</div>
          <div class="content-text">
            Le alternative al trattamento fisioterapico possono includere: terapia farmacologica, interventi chirurgici, altre terapie complementari. Il fisioterapista ha illustrato le ragioni per cui il trattamento fisioterapico rappresenta, nel caso specifico, l'opzione terapeutica più appropriata.
          </div>
        </div>

        <div class="section">
          <div class="section-title">COLLABORAZIONE DEL PAZIENTE</div>
          <div class="content-text">
            Il successo del trattamento dipende anche dalla collaborazione attiva del paziente, che si impegna a:
            <ul>
              <li>Seguire scrupolosamente le indicazioni del fisioterapista</li>
              <li>Eseguire gli esercizi domiciliari prescritti</li>
              <li>Comunicare tempestivamente variazioni del quadro clinico</li>
              <li>Rispettare gli appuntamenti programmati</li>
              <li>Adottare le modifiche dello stile di vita consigliate</li>
            </ul>
          </div>
        </div>

        <div class="checkbox-section">
          <div class="section-title">DICHIARAZIONI E CONSENSO</div>
          <div style="margin-bottom: 15px;">
            <strong>☑</strong> Dichiaro di aver ricevuto informazioni chiare e complete sulla natura del trattamento fisioterapico, sui benefici attesi, sui rischi e sulle possibili alternative terapeutiche.
          </div>
          <div style="margin-bottom: 15px;">
            <strong>☑</strong> Dichiaro di aver avuto la possibilità di porre domande e di aver ricevuto risposte soddisfacenti.
          </div>
          <div style="margin-bottom: 15px;">
            <strong>☑</strong> Sono consapevole che nessun trattamento può garantire risultati certi al 100%.
          </div>
          <div style="margin-bottom: 15px;">
            <strong>☑</strong> Acconsento al trattamento fisioterapico proposto dal Dott. Peppino S. Papa.
          </div>
        </div>

        <div class="section">
          <div class="section-title">CONSENSO AL TRATTAMENTO DEI DATI PERSONALI</div>
          <div class="content-text">
            Ai sensi del Regolamento UE 2016/679 (GDPR), acconsento al trattamento dei miei dati personali e sanitari per le finalità connesse all'erogazione delle prestazioni sanitarie, alla gestione amministrativa e contabile, agli adempimenti di legge. I dati saranno trattati con modalità cartacee e informatiche da personale autorizzato e non saranno comunicati a terzi senza il mio consenso, salvo obblighi di legge.
          </div>
          <div style="margin-top: 15px;">
            <strong>☑</strong> Acconsento al trattamento dei miei dati personali e sanitari per le finalità sopra indicate.
          </div>
        </div>

        <div class="signature-section">
          <div>
            <div style="margin-bottom: 20px;">
              <strong>Data:</strong> <span style="border-bottom: 1px solid #333; padding-bottom: 2px; margin-left: 10px;">${new Date(formData.data).toLocaleDateString('it-IT')}</span>
            </div>
            <div style="margin-bottom: 20px;">
              <strong>Luogo:</strong> <span style="border-bottom: 1px solid #333; padding-bottom: 2px; margin-left: 10px;">Terme Vigliatore (ME)</span>
            </div>
            <div class="signature-box">
              <strong>Firma del Paziente</strong><br>
              <small>(o del rappresentante legale)</small>
            </div>
          </div>
          <div>
            <div style="margin-bottom: 60px;"></div>
            <div class="signature-box">
              <strong>Firma del Fisioterapista</strong><br>
              <small>Dott. Peppino S. Papa</small>
            </div>
          </div>
        </div>

        <div style="margin-top: 40px; font-size: 12px; color: #666; text-align: center;">
          <p>Questo documento è stato generato elettronicamente dal Centro di Riabilitazione Papa</p>
          <p>Via Nazionale Terme, 191 - 98050 Terme Vigliatore (ME) - Tel: 347.5391433</p>
        </div>
      </body>
      </html>
    `;

    // Apre una nuova finestra per la stampa
    const finestraPrint = window.open('', '_blank');
    if (finestraPrint) {
      finestraPrint.document.write(contenutoHTML);
      finestraPrint.document.close();
      finestraPrint.focus();
      
      // Avvia automaticamente la stampa dopo un breve delay
      setTimeout(() => {
        finestraPrint.print();
      }, 500);
      
      toast.success("Modulo di consenso generato! Usa Ctrl+P per stampare o salvare come PDF.");
    } else {
      toast.error("Impossibile aprire la finestra di stampa. Verifica le impostazioni del browser.");
    }
  };

  const isFormValid = () => {
    return formData.nome && 
           formData.cognome && 
           formData.dataNascita && 
           formData.codiceFiscale && 
           formData.telefono && 
           formData.consensoTrattamento && 
           formData.consensoDati;
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <Card className="medical-card">
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <FileText className="h-5 w-5 text-primary" />
            <span>Consenso Informato per Trattamento Fisioterapico</span>
          </CardTitle>
          <CardDescription>
            Compila i tuoi dati per generare il modulo di consenso informato personalizzato
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Dati Anagrafici */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-primary">Dati Anagrafici</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="nome">Nome *</Label>
                <Input
                  id="nome"
                  value={formData.nome}
                  onChange={(e) => handleInputChange('nome', e.target.value)}
                  placeholder="Il tuo nome"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="cognome">Cognome *</Label>
                <Input
                  id="cognome"
                  value={formData.cognome}
                  onChange={(e) => handleInputChange('cognome', e.target.value)}
                  placeholder="Il tuo cognome"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="dataNascita">Data di Nascita *</Label>
                <Input
                  id="dataNascita"
                  type="date"
                  value={formData.dataNascita}
                  onChange={(e) => handleInputChange('dataNascita', e.target.value)}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="luogoNascita">Luogo di Nascita</Label>
                <Input
                  id="luogoNascita"
                  value={formData.luogoNascita}
                  onChange={(e) => handleInputChange('luogoNascita', e.target.value)}
                  placeholder="Città di nascita"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="codiceFiscale">Codice Fiscale *</Label>
                <Input
                  id="codiceFiscale"
                  value={formData.codiceFiscale}
                  onChange={(e) => handleInputChange('codiceFiscale', e.target.value.toUpperCase())}
                  placeholder="RSSMRA80A01H501Z"
                  maxLength={16}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="telefono">Telefono *</Label>
                <Input
                  id="telefono"
                  type="tel"
                  value={formData.telefono}
                  onChange={(e) => handleInputChange('telefono', e.target.value)}
                  placeholder="347.1234567"
                  required
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="indirizzo">Indirizzo Completo</Label>
              <Input
                id="indirizzo"
                value={formData.indirizzo}
                onChange={(e) => handleInputChange('indirizzo', e.target.value)}
                placeholder="Via, numero civico, città, provincia, CAP"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                placeholder="tua.email@esempio.com"
              />
            </div>
          </div>

          <Separator />

          {/* Consensi */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-primary">Consensi Obbligatori</h3>
            
            <div className="space-y-4 p-4 bg-muted/30 rounded-lg">
              <div className="flex items-start space-x-3">
                <Checkbox
                  id="consensoTrattamento"
                  checked={formData.consensoTrattamento}
                  onCheckedChange={(checked) => handleInputChange('consensoTrattamento', checked as boolean)}
                />
                <div className="space-y-1">
                  <Label htmlFor="consensoTrattamento" className="text-sm font-medium leading-relaxed">
                    Consenso al Trattamento Fisioterapico *
                  </Label>
                  <p className="text-sm text-muted-foreground">
                    Dichiaro di aver ricevuto informazioni complete sul trattamento fisioterapico, 
                    sui benefici attesi, sui rischi e sulle alternative terapeutiche. 
                    Acconsento al trattamento proposto dal Dott. Peppino S. Papa.
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-3">
                <Checkbox
                  id="consensoDati"
                  checked={formData.consensoDati}
                  onCheckedChange={(checked) => handleInputChange('consensoDati', checked as boolean)}
                />
                <div className="space-y-1">
                  <Label htmlFor="consensoDati" className="text-sm font-medium leading-relaxed">
                    Consenso al Trattamento dei Dati Personali (GDPR) *
                  </Label>
                  <p className="text-sm text-muted-foreground">
                    Ai sensi del Regolamento UE 2016/679, acconsento al trattamento dei miei dati 
                    personali e sanitari per le finalità connesse all'erogazione delle prestazioni 
                    sanitarie e agli adempimenti di legge.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <Separator />

          {/* Data */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-primary">Data del Consenso</h3>
            <div className="flex items-center space-x-4">
              <Calendar className="h-5 w-5 text-primary" />
              <div className="space-y-2">
                <Label htmlFor="data">Data</Label>
                <Input
                  id="data"
                  type="date"
                  value={formData.data}
                  onChange={(e) => handleInputChange('data', e.target.value)}
                />
              </div>
            </div>
          </div>

          <Separator />

          {/* Pulsante Genera */}
          <div className="text-center space-y-4">
            <Button 
              onClick={generaPDF}
              disabled={!isFormValid()}
              size="lg"
              className="medical-gradient text-white"
            >
              <Download className="mr-2 h-5 w-5" />
              Genera e Scarica Consenso Informato
            </Button>
            
            {!isFormValid() && (
              <p className="text-sm text-muted-foreground">
                Compila tutti i campi obbligatori (*) e accetta i consensi per generare il modulo
              </p>
            )}
            
            <div className="text-xs text-muted-foreground max-w-2xl mx-auto">
              <p>
                Il modulo generato sarà in formato stampabile/PDF. Potrai stamparlo direttamente 
                o salvarlo come PDF utilizzando la funzione di stampa del browser. 
                Il documento dovrà essere firmato e consegnato durante la prima visita.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Informazioni Aggiuntive */}
      <Card className="medical-card">
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <User className="h-5 w-5 text-primary" />
            <span>Informazioni Importanti</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h4 className="font-semibold text-primary mb-2">Prima della Visita</h4>
              <ul className="text-sm text-muted-foreground space-y-1">
                <li>• Porta il modulo firmato alla prima visita</li>
                <li>• Porta eventuali referti medici</li>
                <li>• Indossa abbigliamento comodo</li>
                <li>• Arriva 10 minuti prima dell'appuntamento</li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-primary mb-2">Documenti Necessari</h4>
              <ul className="text-sm text-muted-foreground space-y-1">
                <li>• Documento di identità valido</li>
                <li>• Tessera sanitaria</li>
                <li>• Prescrizione medica (se disponibile)</li>
                <li>• Consenso informato firmato</li>
              </ul>
            </div>
          </div>
          
          <div className="mt-6 p-4 bg-primary/5 rounded-lg">
            <p className="text-sm text-primary font-medium">
              💡 Per domande sul consenso informato o sul trattamento, contattaci al 347.5391433
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ConsensoInformato;